/**
 * The slim exit bar D8 requires once the shell's own header is hidden for the
 * whole Play screen — PHONE ONLY (the model's own file comment): a left
 * "Back" (the only way out while `chromeHidden` is true — the app's bottom
 * tabs are already hidden on this route and the hidden header took the
 * hamburger with it), a one-string centre title, and the sound toggle (the
 * desktop rail toolbar carries Sound/Theme instead, but desktop never mounts
 * this unit at all).
 *
 * The confirm dialog's open flag is local state, same as `ResignTile.View`:
 * the model owns only the action.
 *
 * `useSoundTileModel` is reused rather than `<SoundTile/>` itself: that tile
 * is sized for the 60 px toolbar grid, and this bar's budget is 44 px (the
 * review's "must not steal board height needlessly") — same state, a
 * compact `ui/Button` instead.
 */
import React, {useCallback, useState} from 'react';
import {StyleSheet, View} from 'react-native';

import {useSoundTileModel} from '../../rail/live/Toolbar/tiles/SoundTile.model';
import {SPACE} from '../../theme/tokens';
import {useV2Theme} from '../../theme/useV2Theme';
import {Button, ConfirmDialog, Txt} from '../../ui';
import {PlayHeaderModel} from './model';

/** local, not `theme/tokens.ts` SIZE — out of scope for this task (C1 note). */
const HEIGHT = 44;

/** `BottomControlsComponent`-style copy (D8's own rationale), verbatim per spec C2. */
export const LEAVE_GAME_CONFIRM_COPY =
  'Leave the game? It keeps running without you — you can come back from Play.';

export default React.memo(function PlayHeaderView({
  title,
  needsConfirm,
  onBack,
}: PlayHeaderModel) {
  const tokens = useV2Theme(s => s.tokens);
  const [asking, setAsking] = useState(false);
  const {soundOn, onPress: onToggleSound} = useSoundTileModel();

  const onPressBack = useCallback(() => {
    if (needsConfirm) {
      setAsking(true);
    } else {
      onBack();
    }
  }, [needsConfirm, onBack]);
  const onConfirm = useCallback(() => {
    setAsking(false);
    onBack();
  }, [onBack]);
  const onCancel = useCallback(() => setAsking(false), []);

  return (
    <>
      <View
        testID="v2-play-header"
        style={[
          styles.bar,
          {boxShadow: `inset 0 -1px 0 ${tokens.line}`} as any,
        ]}>
        <Button
          label="Back"
          accessibilityLabel="Back"
          icon="prev"
          variant="ghost"
          size="sm"
          onPress={onPressBack}
        />
        <Txt variant="label" tone="ink2" numberOfLines={1} style={styles.title}>
          {title}
        </Txt>
        <Button
          label={soundOn ? 'Sound' : 'Muted'}
          accessibilityLabel={soundOn ? 'Mute sound' : 'Unmute sound'}
          icon={soundOn ? 'sound' : 'mute'}
          variant="ghost"
          size="sm"
          onPress={onToggleSound}
        />
      </View>
      <ConfirmDialog
        testID="v2-confirm-leave-game"
        visible={asking}
        title={LEAVE_GAME_CONFIRM_COPY}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </>
  );
});

const styles = StyleSheet.create({
  bar: {
    height: HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACE.sm,
    paddingHorizontal: SPACE.xl,
  },
  title: {
    flex: 1,
    textAlign: 'center',
  },
});
